import GhostContentAPI from "@tryghost/content-api";
import type { Author, PostOrPage, Tag } from "@tryghost/content-api";

import { POSTS_PER_PAGE } from "@/lib/blog/paths";
import { getTopic, topicFilter, type BlogTopic } from "@/lib/blog/topics";

const ghost = new GhostContentAPI({
  url: "https://crayo-ai.ghost.io",
  key: process.env.GHOST_KEY || "",
  version: "v5.109.0",
});

export type BlogTag = { slug: string; name: string };
export type BlogAuthor = { slug: string; name: string };

export type BlogPostSummary = {
  slug: string;
  title: string;
  feature_image: string | null;
  published_at: string;
  updated_at: string;
  excerpt: string;
  reading_time: number;
  primary_tag: BlogTag | null;
  tags: BlogTag[];
  primary_author: BlogAuthor | null;
};

export type BlogPost = BlogPostSummary & {
  html: string;
  meta_description: string | null;
  feature_image_alt: string | null;
};

export type PostPage = {
  posts: BlogPostSummary[];
  page: number;
  pages: number;
  total: number;
};

const SUMMARY_FIELDS = [
  "slug",
  "title",
  "feature_image",
  "published_at",
  "updated_at",
  "excerpt",
  "reading_time",
  "primary_tag",
  "primary_author",
];

const toTag = (tag: Tag | null | undefined): BlogTag | null =>
  tag?.slug ? { slug: tag.slug, name: tag.name ?? tag.slug } : null;

const toAuthor = (author: Author | null | undefined): BlogAuthor | null =>
  author?.slug ? { slug: author.slug, name: author.name ?? author.slug } : null;

const toSummary = (post: PostOrPage): BlogPostSummary => {
  const published = post.published_at ?? post.created_at ?? "";
  return {
    slug: post.slug,
    title: post.title ?? "",
    feature_image: post.feature_image ?? null,
    published_at: published,
    updated_at: post.updated_at ?? published,
    excerpt: post.excerpt ?? "",
    reading_time: post.reading_time ?? 0,
    primary_tag: toTag(post.primary_tag ?? post.tags?.[0]),
    tags: (post.tags ?? []).map(toTag).filter((tag): tag is BlogTag => tag !== null),
    primary_author: toAuthor(post.primary_author ?? post.authors?.[0]),
  };
};

const toPost = (post: PostOrPage): BlogPost => ({
  ...toSummary(post),
  html: post.html ?? "",
  meta_description: post.meta_description ?? null,
  feature_image_alt: post.feature_image_alt ?? null,
});

export const ghostImage = (url: string | null | undefined, width: number): string => {
  if (!url) return "";
  if (!url.includes("/content/images/")) return url;
  if (url.includes("/content/images/size/")) return url;
  return url.replace("/content/images/", `/content/images/size/w${width}/`);
};

export async function getPostPage(page: number, filter?: string): Promise<PostPage | null> {
  try {
    const result = await ghost.posts.browse({
      fields: SUMMARY_FIELDS,
      include: ["tags", "authors"],
      limit: POSTS_PER_PAGE,
      page,
      order: "published_at desc",
      ...(filter ? { filter } : {}),
    });

    const pagination = result.meta?.pagination;
    return {
      posts: result.map(toSummary),
      page: pagination?.page ?? page,
      pages: pagination?.pages ?? 1,
      total: pagination?.total ?? result.length,
    };
  } catch (err) {
    console.error("ghost: failed to load post page", err);
    return null;
  }
}

export async function getTopicPage(topicSlug: string, page: number): Promise<PostPage | null> {
  const topic = getTopic(topicSlug);
  if (!topic) return null;
  return getPostPage(page, topicFilter(topic));
}

export async function getAuthorPage(authorSlug: string, page: number): Promise<PostPage | null> {
  return getPostPage(page, `primary_author:${authorSlug}`);
}

export async function getAuthor(slug: string): Promise<BlogAuthor | null> {
  try {
    const author = await ghost.authors.read({ slug }, { fields: ["slug", "name"] });
    return toAuthor(author);
  } catch (err) {
    console.error("ghost: failed to load author", err);
    return null;
  }
}

export async function getPost(slug: string): Promise<BlogPost | null> {
  try {
    const post = await ghost.posts.read({ slug }, { include: ["tags", "authors"] });
    return post?.slug ? toPost(post) : null;
  } catch (err) {
    console.error("ghost: failed to load post", err);
    return null;
  }
}

export async function getRelatedPosts(
  post: Pick<BlogPost, "slug" | "tags" | "primary_tag">,
  limit = 3,
): Promise<BlogPostSummary[]> {
  const tagSlugs = post.tags.map((tag) => tag.slug);
  const chosen: BlogPostSummary[] = [];
  const seen = new Set<string>([post.slug]);

  if (tagSlugs.length) {
    const candidates = await getPostPage(1, `tags:[${tagSlugs.join(",")}]+slug:-${post.slug}`);
    const pool = (candidates?.posts ?? []).filter((candidate) => !seen.has(candidate.slug));

    const score = (candidate: BlogPostSummary) => {
      const sharesPrimary =
        post.primary_tag &&
        candidate.tags.some((tag) => tag.slug === post.primary_tag?.slug) &&
        candidate.primary_tag?.slug === post.primary_tag.slug;
      const shared = candidate.tags.filter((tag) => tagSlugs.includes(tag.slug)).length;
      return { primary: sharesPrimary ? 1 : 0, shared };
    };

    pool.sort((a, b) => {
      const sa = score(a);
      const sb = score(b);
      if (sa.primary !== sb.primary) return sb.primary - sa.primary;
      if (sa.shared !== sb.shared) return sb.shared - sa.shared;
      const dateDiff = Date.parse(b.published_at) - Date.parse(a.published_at);
      if (dateDiff) return dateDiff;
      return a.slug.localeCompare(b.slug);
    });

    for (const candidate of pool) {
      if (chosen.length >= limit) break;
      chosen.push(candidate);
      seen.add(candidate.slug);
    }
  }

  if (chosen.length < limit) {
    const latest = await getPostPage(1);
    for (const candidate of latest?.posts ?? []) {
      if (chosen.length >= limit) break;
      if (seen.has(candidate.slug)) continue;
      chosen.push(candidate);
      seen.add(candidate.slug);
    }
  }

  return chosen.slice(0, limit);
}

export async function getAllPostSlugs(): Promise<
  { slug: string; published_at: string; updated_at: string }[]
> {
  const slugs: { slug: string; published_at: string; updated_at: string }[] = [];

  for (let page = 1; page <= 100; page++) {
    let batch;
    try {
      batch = await ghost.posts.browse({
        fields: ["slug", "published_at", "updated_at"],
        limit: 100,
        page,
        order: "published_at desc",
      });
    } catch (err) {
      console.error("ghost: failed to load post slugs", err);
      break;
    }

    if (!batch || batch.length === 0) break;

    for (const post of batch) {
      if (!post.slug) continue;
      const published = post.published_at ?? post.created_at ?? "";
      slugs.push({
        slug: post.slug,
        published_at: published,
        updated_at: post.updated_at ?? published,
      });
    }

    const pages = batch.meta?.pagination?.pages ?? page;
    if (page >= pages) break;
  }

  return slugs;
}

export async function getTopicLastmod(topic: BlogTopic): Promise<string | null> {
  try {
    const result = await ghost.posts.browse({
      fields: ["updated_at"],
      filter: topicFilter(topic),
      limit: 1,
      order: "updated_at desc",
    });
    return result[0]?.updated_at ?? null;
  } catch (err) {
    console.error("ghost: failed to load topic lastmod", err);
    return null;
  }
}
